
import {
    useLayoutEffect,
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    FlatList,
    ScrollView,
    TouchableOpacity,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    GetNewsResquestType,
    NewsData
} from "../../../services/restAPI/actions/getNews/types";
import type {
    NewsCategoriesObjectType
} from "../../../services/restAPI/actions/getNewsCategories/types";
import type AppStackParamList from "../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    NewspaperIcon,
    ChevronLeft
} from "lucide-react-native";
import {
    NewsCard
} from "../../../components";
import {
    RESTService
} from "../../../services/restAPI";
import {
    storage
} from "../../../storage";
import {
    formatDate
} from "../../../utils";

const News = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [loading,
        setLoading] = useState(true);
    const [selected,
        setSelected] = useState<string>("latest");
    const [newsCategoriesData,
        setNewsCategoriesData] = useState<Array<NewsCategoriesObjectType>>([]);
    const [newsData,
        setNewsData] = useState<NewsData[]>([]);
    const [page,
        setPage] = useState(1);
    const [endReached,
        setEndReached] = useState(false);
    const [searchLoading,
        setSearchLoading] = useState(false);

    const inset = useSafeAreaInsets();

    useEffect(() => {
        getNewsCategories();
        getNews();
    }, []);

    useEffect(() => {
        if(!loading) {
            setPage(1);
            setEndReached(false);
            setSearchLoading(false);
            getNews();
        }
    }, [selected]);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => renderHeader()
        });
    }, [
        navigation
    ]);

    const getNews = (_page?: number) => {
        if(!searchLoading) setSearchLoading(true);

        const params: GetNewsResquestType = {
            language: storage.getString("language") ?? "tr-TR"
        };

        if(selected !== "latest") {
            params.categories= [selected];
        }

        if(_page) {
            params.page = _page;
        }
        RESTService.action("GetNews", params)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    if(_page) {
                        setPage(_page);
                        const _data = JSON.parse(JSON.stringify(newsData));
                        const _newsData = res.payload.filter(newItem => !_data.some((oldItem: { _id: string }) => oldItem._id === newItem._id));
                        setNewsData([
                            ..._data,
                            ..._newsData
                        ]);

                    } else {
                        setNewsData(res.payload);
                    }

                    if(res.payload.length < 9) {
                        setEndReached(true);
                    }
                } else {
                    if(!_page) {
                        setNewsData([]);
                    }
                    setEndReached(true);
                }

                setSearchLoading(false);
            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                setLoading(false);
            });
    };

    const getNewsCategories = () => {
        RESTService.action("GetNewsCategories", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setNewsCategoriesData([
                        {
                            _id: "static-latest",
                            key: "latest",
                            titleKey: "latest"
                        },
                        ...res.payload
                    ]);
                }
            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                setLoading(false);
            });
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    paddingTop: inset.top + spaces.spacingMd,
                    paddingBottom: spaces.spacingMd,
                    paddingHorizontal: spaces.spacingMd
                }
            ]}
        >
            <TouchableOpacity
                style={[
                    stylesheet.backButton,
                    {
                        padding: spaces.spacingSm
                    }
                ]}
                onPress={() => {
                    navigation.goBack();
                }}
            >
                <ChevronLeft
                    size={30}
                />
            </TouchableOpacity>
            <View
                style={[
                    stylesheet.headerTitleContainer,
                    {
                        top: inset.top + spaces.spacingSm
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                    style={stylesheet.headerTitle}
                >
                    {localize("news")}
                </Text>
            </View>
        </View>;
    };

    const renderItemNews = ({
        item
    }: { item: NewsData}) => {
        let displayTitle = "";
        if (item.categoryIDs && item.categoryIDs.length > 0) {
            const titles = item.categoryIDs
                .map(id => newsCategoriesData.find(c => c._id === id)?.titleKey)
                .filter(Boolean)
                .map(key => localize(key as Parameters<typeof localize>[0]));

            displayTitle = titles.join(", ");
            if (displayTitle.length > 30) {
                displayTitle = `${displayTitle.substring(0, 27)}...`;
            }
        }

        return <NewsCard
            _id={item._id}
            createdAt= {formatDate(item.createdAt)}
            shortContent={item.shortContent}
            title= {item.title}
            coverPhotoURL={item.coverPhotoURL}
            onPress={() => {
                navigation.navigate("NewsDetail", {
                    newsID: item._id
                });
            }}
            type={displayTitle}
        />;
    };

    const renderCategories = () => {
        return <ScrollView
            horizontal={true}
            showsHorizontalScrollIndicator={false}
            style={{
                marginBottom: spaces.spacingLg
            }}
        >
            {newsCategoriesData.map((item, index) => {
                const isSelected = selected === item.key;
                const isLastItem = index === newsCategoriesData.length - 1;
                const isNextSelected = index + 1 < newsCategoriesData.length && (selected as string) === newsCategoriesData[index + 1]?.key;
                return <View
                    key={item._id}
                    style={stylesheet.filterItemContainer}
                >
                    <TouchableOpacity
                        onPress={() => setSelected(item.key)}
                        style={[
                            {
                                backgroundColor: isSelected ? colors.project.secondary : "transparent",
                                borderRadius: radiuses.full,
                                marginRight: isLastItem ? 0 : spaces.spacingMd,
                                paddingHorizontal: spaces.spacingLg,
                                paddingVertical: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            variant={isSelected ? "titleMediumSize" : "bodyMediumSize"}
                            color={isSelected ? "onPrimary" : "mid"}
                            style={stylesheet.filterText}
                        >
                            {localize(item.titleKey)}
                        </Text>
                    </TouchableOpacity>
                    {!isLastItem && !isSelected && !isNextSelected && (
                        <View
                            style={[
                                stylesheet.filterSeparator,
                                {
                                    backgroundColor: colors.content.border.subtle,
                                    marginRight: spaces.spacingMd
                                }
                            ]}
                        />)}
                </View>;
            })}
        </ScrollView>;
    };

    const renderNews = () => {
        return <View
            style={{
                marginBottom: spaces.spacingXl * 4
            }}
        >
            {renderCategories()}
            <FlatList
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                keyExtractor={(item) => item._id}
                onEndReachedThreshold={0.7}
                onEndReached={() => {
                    if(endReached) {
                        return;
                    }
                    if(searchLoading) {
                        return;
                    }
                    getNews(page + 1);
                }}
                renderItem={({
                    item
                }) => {
                    return renderItemNews({
                        item: item
                    });
                }}
                horizontal={false}
                data={newsData}
                ListEmptyComponent={renderNotFound}
            />
        </View>;
    };

    const renderNotFound = () => {
        return <View
            style={[
                stylesheet.notFoundContainer,
                {
                    backgroundColor: colors.content.container.subtle,
                    paddingVertical: spaces.spacingXl * 2,
                    paddingHorizontal: spaces.spacingXl,
                    marginTop: spaces.spacingXl,
                    borderRadius: radiuses.xl
                }
            ]}
        >
            <NewspaperIcon
                color={colors.content.icon.mid}
                size={48}
            />
            <Text
                variant="titleLargeSize"
                color="high"
                style={[
                    stylesheet.notFoundText,
                    {
                        marginTop: spaces.spacingLg
                    }
                ]}
            >
                {localize("not-found") || "Haber Bulunamadı"}
            </Text>
            <Text
                variant="bodyMediumSize"
                color="mid"
                style={[
                    stylesheet.notFoundText,
                    {
                        marginTop: spaces.spacingSm
                    }
                ]}
            >
                {"Bu kategoride henüz haber bulunmuyor."}
            </Text>
        </View>;
    };

    if(loading) {
        return <PageContainer
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                size={40}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <PageContainer
        isScrollable={false}
    >
        {renderNews()}
    </PageContainer>;
};
export default News;
